// Bootstraps the authenticated session and provides an ApiClient to the tree.
//
// Normal tenant flow:
//   1. On mount, call GET /api/me with only the Clerk JWT. /me auto-provisions a
//      Restaurant + User on first sign-in, then returns { restaurant, employee, role }.
//   2. Create an ApiClient with the JWT + x-restaurant-id header.
//   3. Expose the profile via useProfile().
//
// PA-3 platform-staff flow:
//   /api/me returns { isPlatformStaff:true, restaurant:null } (the Staff org is never a
//   tenant). We fetch GET /api/platform/restaurants and show a picker; on selection we
//   build the client with x-view-restaurant-id (read-only + audited server-side) and show
//   a read-only banner. The selected restaurant is synthesized into the profile so the rest
//   of the app renders normally.
//
// Must be rendered inside Clerk's <SignedIn> so useAuth() always has a session.
import React, {
  createContext,
  lazy,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useAuth, OrganizationSwitcher } from '@clerk/clerk-react'
import { ApiClient, ApiError } from '../api/client'
import { fetchPlatformRestaurants, provisionTenant, type PlatformRestaurant } from '../api/platform'
import { StaffRestaurantPicker } from '../components/platform/StaffRestaurantPicker'
import { StaffBanner } from '../components/platform/StaffBanner'
import { OfflineBanner } from '../components/pos/OfflineBanner'

// m0-5c-1 — the platform-admin console is lazy-loaded so ApiContext and the page don't import each
// other at module-eval time (the page imports useApi from here).
const PlatformConsolePage = lazy(() => import('../pages/platform/PlatformConsolePage'))

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UserProfile {
  clerkUserId: string
  role: string
  restaurant: { id: string; name: string; timezone: string }
  employee: {
    id: string
    firstName: string
    lastName: string
    position: string
    status: string
    hourlyRate: string | null
  } | null
  // HR Foundation v0 — the stations this user is responsible for, and what they
  // may do, right now (PRD §6). Owner/GM → every station. Optional for backward-compat.
  responsibleLocationIds?: string[]
  permissions?: string[]
  // May reopen a closed accounting month: the Owner, or a GM whose own role holds accounting.post — computed by the
  // server with the same rule the reopen route enforces (Thomas 2026-10-07). False once employment has ended.
  canReopenPeriods?: boolean
  // PA-3 — true when the caller is a TOC platform-staff observer (read-only cross-tenant).
  isPlatformStaff?: boolean
  // m0-5c-1 — true when a platform-staff caller ALSO holds the platform.admin grant (gates the console).
  isPlatformAdmin?: boolean
  // DEF-80c — true while a platform admin is acting inside the viewed restaurant with owner-level access.
  actingAsPlatformAdmin?: boolean
}

// Raw GET /api/me shape — restaurant is null for platform staff until they target one, and for a
// brand-new org whose tenant has not been provisioned yet (m0-2b: needsProvisioning === true).
type MeResponse = Omit<UserProfile, 'restaurant'> & {
  restaurant: { id: string; name: string; timezone: string } | null
  needsProvisioning?: boolean
}

// DEF-M0-2b-1/-7 — POST /api/platform/provision returns 409 `no_active_organization` when the session
// has NO org the backend could resolve: zero memberships, or two-or-more (the auth plugin fails closed
// rather than guess). The common SOLE-membership case is resolved server-side and never reaches here.
// This is a RECOVERABLE state, not a dead error — the user picks or creates an organization (Clerk's
// OrganizationSwitcher) and, because selecting one hydrates useAuth().orgId (the bootstrap re-key),
// /api/me re-runs automatically. Classified so the error screen can offer that path instead of a dead
// string. Exported for a focused unit test (the render itself is E2E-covered).
export function isNoActiveOrgError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.message === 'no_active_organization'
}

interface ApiContextValue {
  client:  ApiClient
  profile: UserProfile
}

// DEF-80c — the profile the app renders. Normal users: their own. Read-only staff: their staff profile
// pointed at the viewed restaurant. A platform admin acting inside the viewed restaurant: the acting
// membership's profile from GET /api/me with x-view-restaurant-id (OWNER role + permissions), flagged
// actingAsPlatformAdmin. Pure, exported for unit tests (the provider render itself is E2E-covered).
export function effectiveProfile(
  me: MeResponse,
  viewRestaurant: { id: string; name: string } | null,
  actingMe: MeResponse | null,
): UserProfile {
  if (me.isPlatformStaff && viewRestaurant) {
    if (actingMe?.actingAsPlatformAdmin && actingMe.restaurant && actingMe.restaurant.id === viewRestaurant.id) {
      return { ...actingMe, restaurant: actingMe.restaurant }
    }
    return { ...me, restaurant: { id: viewRestaurant.id, name: viewRestaurant.name, timezone: 'UTC' } }
  }
  return { ...me, restaurant: me.restaurant as { id: string; name: string; timezone: string } }
}

// DEF-81 — true while a platform admin has chosen a restaurant but the acting check (/api/me through the view
// client) has not finished for it yet. Pure, exported for unit tests.
export function actingCheckPending(
  isStaff: boolean,
  isAdmin: boolean,
  viewRestaurantId: string | null,
  actingCheckedFor: string | null,
): boolean {
  return isStaff && isAdmin && viewRestaurantId !== null && actingCheckedFor !== viewRestaurantId
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ApiContext = createContext<ApiContextValue | null>(null)

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ApiProvider({ children }: { children: React.ReactNode }) {
  // orgId is the ACTIVE Clerk org. Watching it means switching orgs (via the
  // OrganizationSwitcher) re-bootstraps /api/me — so flipping to the "Staff" org
  // flips the app into platform-staff mode without a manual reload.
  const { getToken, orgId } = useAuth()

  const [me, setMe]           = useState<MeResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState<string | null>(null)
  // DEF-M0-2b-7/-3 — true when provisioning was refused for lack of a resolvable active org (the 409),
  // OR the post-provision refetch still reports needsProvisioning. Recoverable via the org picker below.
  const [needsOrg, setNeedsOrg] = useState(false)

  // PA-3b — platform-staff "view any restaurant" selection + picker list.
  const [viewRestaurant, setViewRestaurant] = useState<PlatformRestaurant | null>(null)
  const [restaurants, setRestaurants]       = useState<PlatformRestaurant[]>([])
  const [listLoading, setListLoading]       = useState(false)
  const [listError, setListError]           = useState<string | null>(null)
  // m0-5c-1 — a platform.admin staff member can open the cross-tenant console before targeting a tenant.
  const [consoleOpen, setConsoleOpen]       = useState(false)
  // DEF-80c — the acting (owner-level) profile for a platform admin inside the viewed restaurant.
  const [actingMe, setActingMe]             = useState<MeResponse | null>(null)
  // DEF-81 — the restaurant id whose acting check has FINISHED (success or failure). Until it equals the
  // chosen restaurant the app shows the "Opening … as TOC Admin" spinner. A loading flag set inside the
  // effect was one render late: the pages mounted read-only, fired their requests, were unmounted by the
  // spinner and then remounted as TOC Admin and fetched everything again.
  const [actingCheckedFor, setActingCheckedFor] = useState<string | null>(null)

  // Bootstrap client — JWT only. Used for GET /api/me and the platform restaurant list.
  // orgId is an intentional re-bootstrap key: it isn't used in the factory, but including it
  // re-creates the client (and re-runs the /me effect) when the active org changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const bootstrapClient = useMemo(() => new ApiClient(getToken), [getToken, orgId])

  // Switching orgs clears any staff view target (fresh picker in the new context).
  useEffect(() => { setViewRestaurant(null) }, [orgId])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setNeedsOrg(false)

    // m0-2b: /api/me no longer lazily creates a tenant. A brand-new org signs in with
    // needsProvisioning === true — call POST /api/platform/provision (the ONE creation path)
    // then re-fetch the now-complete profile. Platform staff (restaurant intentionally null)
    // are never provisioned.
    const bootstrap = async () => {
      let res = await bootstrapClient.get<{ data: MeResponse }>('/me')
      if (res.data.needsProvisioning && !res.data.isPlatformStaff) {
        await provisionTenant(bootstrapClient)
        res = await bootstrapClient.get<{ data: MeResponse }>('/me')
        // DEF-M0-2b-3 — if provisioning did NOT resolve the tenant (still needsProvisioning / no
        // restaurant for a non-staff caller), do not fall through to the unsafe cast below (which
        // would white-screen once). Surface the recoverable org-picker instead.
        if (res.data.needsProvisioning && !res.data.isPlatformStaff && !res.data.restaurant) {
          if (!cancelled) setNeedsOrg(true)
          return
        }
      }
      if (!cancelled) setMe(res.data)
    }

    bootstrap()
      .catch(err => {
        if (!cancelled) {
          // DEF-M0-2b-7 — no active org the backend could resolve: recoverable via the org picker, not a dead error.
          if (isNoActiveOrgError(err)) {
            setNeedsOrg(true)
          } else {
            const msg =
              err instanceof ApiError
                ? `Sign-in error (${err.status}): ${err.message}`
                : (err instanceof Error ? err.message : 'Failed to load profile')
            setError(msg)
          }
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [bootstrapClient])

  const isStaff = me?.isPlatformStaff === true

  // Platform staff with no target chosen yet → load the restaurants for the picker.
  useEffect(() => {
    if (!isStaff || viewRestaurant) return
    let cancelled = false
    setListLoading(true)
    setListError(null)

    fetchPlatformRestaurants(bootstrapClient)
      .then(rs => { if (!cancelled) setRestaurants(rs) })
      .catch(err => {
        if (!cancelled) setListError(err instanceof Error ? err.message : 'Failed to load restaurants')
      })
      .finally(() => { if (!cancelled) setListLoading(false) })

    return () => { cancelled = true }
  }, [isStaff, viewRestaurant, bootstrapClient])

  // Full client — normal users send x-restaurant-id; staff send x-view-restaurant-id.
  const client = useMemo(
    () =>
      isStaff
        ? new ApiClient(getToken, undefined, viewRestaurant?.id)
        : new ApiClient(getToken, me?.restaurant?.id),
    [getToken, isStaff, viewRestaurant?.id, me?.restaurant?.id],
  )

  // DEF-80c — once a platform admin targets a restaurant, fetch /api/me THROUGH the view client (it carries
  // x-view-restaurant-id) to get the acting OWNER-level profile. One request per chosen restaurant: keyed on
  // the restaurant id + the view client, cleared when the target changes. A failure leaves the read-only view.
  const viewId = viewRestaurant?.id
  const isAdmin = me?.isPlatformAdmin === true
  useEffect(() => {
    setActingMe(null)
    // Back on the picker: forget the finished check, so choosing the same restaurant again waits for a new one.
    if (!viewId) setActingCheckedFor(null)
    if (!isStaff || !isAdmin || !viewId) return
    let cancelled = false
    client.get<{ data: MeResponse }>('/me')
      .then((res) => { if (!cancelled) setActingMe(res.data) })
      .catch(() => { /* stay read-only */ })
      .finally(() => { if (!cancelled) setActingCheckedFor(viewId) })
    return () => { cancelled = true }
  }, [isStaff, isAdmin, viewId, client])

  if (loading) {
    return (
      <div style={styles.center}>
        <div style={styles.spinner} />
        <p style={styles.msg}>Setting up your account…</p>
      </div>
    )
  }

  // DEF-M0-2b-7/-3 — no active org the backend could resolve (0 memberships, or 2+), or provisioning
  // did not resolve the tenant. Recoverable: pick or create an organization. Selecting one hydrates
  // useAuth().orgId — the bootstrapClient re-key — so /api/me re-runs automatically (create/select
  // URLs are a belt-and-suspenders reload). Checked before the generic error screen.
  if (needsOrg) {
    return (
      <div style={styles.center}>
        <p style={styles.msg}>Select or create an organization to finish setting up your account.</p>
        <OrganizationSwitcher hidePersonal afterSelectOrganizationUrl="/" afterCreateOrganizationUrl="/" />
      </div>
    )
  }

  if (error || !me) {
    return (
      <div style={styles.center}>
        <p style={{ ...styles.msg, color: '#dc2626' }}>
          {error ?? 'Could not load your profile. Please try signing in again.'}
        </p>
      </div>
    )
  }

  // m0-5c-1 — a platform.admin staff member opened the cross-tenant console (no tenant targeted).
  // The console uses useApi() only; the synthetic restaurant satisfies the profile type (it never
  // reads profile.restaurant). The staff client carries no x-view-restaurant-id — requirePlatformAdmin
  // (Staff-org + grant) is the server gate on the /platform endpoints it calls.
  if (isStaff && !viewRestaurant && consoleOpen) {
    const consoleProfile: UserProfile = { ...me, restaurant: { id: '', name: 'Platform', timezone: 'UTC' } }
    return (
      <ApiContext.Provider value={{ client, profile: consoleProfile }}>
        <Suspense fallback={<div style={styles.center}><div style={styles.spinner} /></div>}>
          <PlatformConsolePage onExit={() => setConsoleOpen(false)} />
        </Suspense>
      </ApiContext.Provider>
    )
  }

  // Platform staff who haven't targeted a tenant yet → full-screen picker.
  if (isStaff && !viewRestaurant) {
    return (
      <StaffRestaurantPicker
        restaurants={restaurants}
        loading={listLoading}
        error={listError}
        onPick={setViewRestaurant}
        onOpenConsole={me.isPlatformAdmin ? () => setConsoleOpen(true) : undefined}
      />
    )
  }

  // DEF-80c / DEF-81 — until the acting check for THIS restaurant has finished, render nothing of the app:
  // no read-only flash, and no page mounting (and fetching) twice.
  if (actingCheckPending(isStaff, isAdmin, viewRestaurant?.id ?? null, actingCheckedFor)) {
    return (
      <div style={styles.center}>
        <div style={styles.spinner} />
        <p style={styles.msg}>Opening {viewRestaurant?.name} as TOC Admin…</p>
      </div>
    )
  }

  // Effective profile: staff reflect the viewed restaurant (owner-level when a platform admin is acting);
  // normal users use their own (the server guarantees a restaurant for non-staff).
  const profile: UserProfile = effectiveProfile(me, viewRestaurant, actingMe)
  const acting = profile.actingAsPlatformAdmin === true

  return (
    <ApiContext.Provider value={{ client, profile }}>
      {isStaff && viewRestaurant && (
        <StaffBanner restaurantName={viewRestaurant.name} acting={acting} onSwitch={() => setViewRestaurant(null)} />
      )}
      {/* FU-POS-6c-1 — offline/queued indicator + reconnect drain. Normal tenants only;
          platform staff are read-only, so they have nothing to queue. */}
      {!isStaff && <OfflineBanner client={client} restaurantId={profile.restaurant.id} />}
      {children}
    </ApiContext.Provider>
  )
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useApi(): ApiClient {
  const ctx = useContext(ApiContext)
  if (!ctx) throw new Error('useApi() must be called inside <ApiProvider>')
  return ctx.client
}

export function useProfile(): UserProfile {
  const ctx = useContext(ApiContext)
  if (!ctx) throw new Error('useProfile() must be called inside <ApiProvider>')
  return ctx.profile
}

// ─── Inline styles (no CSS dependency) ───────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    gap: '1rem',
    background: '#f8fafc',
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid #e2e8f0',
    borderTopColor: '#2563eb',
    borderRadius: '50%',
    animation: 'spin 0.75s linear infinite',
  },
  msg: {
    fontFamily: 'system-ui, sans-serif',
    fontSize: 15,
    color: '#64748b',
    margin: 0,
  },
}
